import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Moment from 'moment';
import MentionableTextArea from '@Common/MentionableTextArea/MentionableTextArea';
import { DATE_FORMAT } from '@Constants/constants';
import { CommentDetailsProps, MentionableUser } from './Types';

interface CommentRowProps {
    comment: CommentDetailsProps;
    mentionableUsers: MentionableUser[];
    isOwner: boolean;
    onCommentPost: (commentId: number, value: string) => Promise<any>;
    onCommentDelete: (commentId: number) => Promise<void>;
}

const CommentRow = (props: CommentRowProps) => {

    const { mentionableUsers, onCommentPost, onCommentDelete, isOwner } = props;

    const [isEditable, setIsEditable] = useState<boolean>(false);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [comment, setComment] = useState<CommentDetailsProps>(props.comment);
    const [commentContent, setCommentContent] = useState<string>(comment.comment);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);

    const currentUserName = useSelector((state: { user: { userDisplayName: string } }) => state.user.userDisplayName);

    useEffect(() => {
        // Editable within 5 minutes and only by the same user
        setIsEditable(Moment.utc().diff(comment.created) < 1000 * 60 * 5 && comment.createdBy === currentUserName);
        setCommentContent(comment.comment);
    }, [comment.created, comment.comment, comment.createdBy, currentUserName]);

    const onModifyCommentSave = async () => {
        const newComment = await onCommentPost(comment.id, commentContent);
        if (!newComment) return;
        setComment(prev => ({
            ...prev,
            ...newComment,
            created: prev.created,
            createdBy: prev.createdBy,
        }));
        setIsEditing(false);
    };

    const cancelModify = () => {
        setIsEditing(false);
        setCommentContent(comment.comment);
    };

    const confirmDelete = async () => {
        setIsDeleting(true);
        try {
            await onCommentDelete(comment.id);
        } finally {
            setIsDeleting(false);
            setShowDeleteConfirm(false);
        }
    };

    return (
        <div className='row'>
            <div className='col-12'>
                <div className='bg-light comment-row'>
                    {isEditing
                        ? <>
                            <div className='edit-comment-container w-100'>
                                <MentionableTextArea
                                    name='comment'
                                    data={mentionableUsers}
                                    value={commentContent}
                                    onChange={(e) => setCommentContent(e.target.value)}
                                    maxLength={2000}
                                    placeHolder='What would you like to say? Type @ to tag a user in your comment.'
                                />
                                <div className='buttons-container'>
                                    <button type='button' title='Save' className='pacer-table-button post-button' onClick={onModifyCommentSave}>
                                        <i className='fa-thin fa-circle-check'></i>
                                    </button>
                                    <button type='button' title='Cancel' className='pacer-table-button post-button' onClick={cancelModify}>
                                        <i className='fa-thin fa-circle-xmark'></i>
                                    </button>
                                </div>
                            </div>
                        </>
                        : <div className='d-flex flex-column'>
                            <span className="comment-rendered text-break" dangerouslySetInnerHTML={{ __html: comment.commentHtml }}></span>

                            <span className='comment-footer mt-2' style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <span>{comment.createdBy} | {Moment.utc(comment.created).format(DATE_FORMAT)}</span>
                                {isEditable && !isEditing && (
                                    <button
                                        type='button'
                                        title='Edit comment'
                                        className='comment-action-btn'
                                        onClick={() => setIsEditing(true)}
                                    >
                                        <i className='fa-thin fa-pencil'></i>
                                    </button>
                                )}
                                {isOwner && (
                                    <button
                                        type='button'
                                        title='Delete comment'
                                        className='comment-action-btn comment-action-btn--delete'
                                        onClick={() => setShowDeleteConfirm(true)}
                                    >
                                        <i className='fa-thin fa-trash'></i>
                                    </button>
                                )}
                            </span>
                            {Moment(comment.created).diff(comment.modified) < 0 &&
                                <span className='comment-footer mt-1'>Edited by {comment.modifiedBy} | {Moment.utc(comment.modified).format(DATE_FORMAT)}</span>
                            }
                        </div>
                    }
                </div>

                {/* Delete confirmation modal */}
                {showDeleteConfirm && (
                    <div className='comment-delete-overlay' onClick={() => setShowDeleteConfirm(false)}>
                        <div className='comment-delete-modal' role='dialog' aria-modal='true' aria-labelledby='comment-delete-title' onClick={(e) => e.stopPropagation()}>
                            <div className='comment-delete-modal__header' id='comment-delete-title'>
                                <i className='fa-thin fa-triangle-exclamation' style={{ color: '#dc3545', marginRight: '8px' }}></i>
                                Delete Comment
                            </div>
                            <div className='comment-delete-modal__body'>
                                Are you sure you want to delete this comment? This action cannot be undone.
                            </div>
                            <div className='comment-delete-modal__footer'>
                                <button
                                    type='button'
                                    className='btn btn-link'
                                    onClick={() => setShowDeleteConfirm(false)}
                                    disabled={isDeleting}
                                >
                                    Cancel
                                </button>
                                <button
                                    type='button'
                                    className='btn btn-danger'
                                    onClick={confirmDelete}
                                    disabled={isDeleting}
                                >
                                    {isDeleting ? 'Deleting…' : 'Delete Comment'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CommentRow;
